import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Loader2, UserPlus, X, CheckCircle2, CreditCard, Users, Shield, User } from 'lucide-react';
import { LEGAL_VERSIONS, isValidPhone, resolveSeatType, validateMembershipSeats } from '@court-sync/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Field,
  FieldLabel,
  Input,
  Separator,
  Badge,
  PhoneInput,
  PhoneNumberLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { cn } from '@/lib/utils';
import type { MembershipType } from '@court-sync/shared';
import { PublicStripePaymentConfirm } from '@/components/public/PublicStripePaymentConfirm';
import { PublicOrgSubpageHero } from '@/components/public/PublicOrgSubpageHero';
import { AuthService } from '@/services/auth';
import { useAuthStore } from '@/store/auth';
import { formatCurrency, type Currency } from '@/utils/currency';
import {
  publicClubMembershipService,
  type PublicMembershipCatalog,
  type PublicMembershipQuoteResponse,
  type PublicMembershipSeatInput,
} from '@/services/publicClubMembership';
import type { MembershipPurchaseResponse } from '@/services/clubMembers';
import { PublicClubRouteShell } from '@/components/public/PublicClubRouteShell';
import { useClubSlug, useClubSlugResolving } from '@/hooks/useClubSlug';
import { useClubPaths } from '@/hooks/useClubPaths';
import { useOrgSlug } from '@/hooks/useOrgSlug';
import { useOrgHost } from '@/contexts/OrgHostContext';
import { useClubHost } from '@/contexts/ClubHostContext';
import { usePublicClubProfile } from '@/queries/publicClubProfile';
import { usePublicOrgProfile } from '@/queries/publicOrgProfile';
import { useEmbedHost } from '@/contexts/EmbedHostContext';
import { buildClubAuthLinkOutUrl } from '@/lib/clubPaths';
import { useDefaultPhoneCountry } from '@/hooks/useDefaultPhoneCountry';
import type { PublicMembershipPrimaryMemberInput } from '@/services/publicClubMembership';

type WizardStep = 'plan' | 'auth' | 'members' | 'payment' | 'success';

/** Matches backend `DEFAULT_JUNIOR_SEAT_MAX_AGE` in membershipService. */
const JUNIOR_SEAT_MAX_AGE = 17;

function buildAnonymousJoinSeatTypes(
  additionalSeats: PublicMembershipSeatInput[],
  asOfDate = new Date()
) {
  return [
    { seatType: 'adult' as const },
    ...additionalSeats.map((seat) => ({
      seatType: resolveSeatType(
        { dateOfBirth: seat.dateOfBirth ? new Date(`${seat.dateOfBirth}T00:00:00Z`) : null },
        asOfDate,
        JUNIOR_SEAT_MAX_AGE
      ),
    })),
  ];
}

function describePlanSeatAllowance(plan: MembershipType): string {
  const adultLabel = `${plan.maxAdults} adult${plan.maxAdults === 1 ? '' : 's'}`;
  const juniorLabel =
    plan.includedJuniors > 0
      ? ` and ${plan.includedJuniors} junior${plan.includedJuniors === 1 ? '' : 's'}`
      : '';
  const extraJuniorsLabel = plan.extraJuniorsFree && plan.includedJuniors > 0 ? ' (more juniors allowed)' : '';
  return `This plan covers up to ${adultLabel}${juniorLabel}${extraJuniorsLabel}. You count as one adult.`;
}
type AuthMode = 'register' | 'login';
type PrimaryGender = 'male' | 'female' | 'other';

function formatCentsAsMoney(cents: number, currency: string): string {
  return formatCurrency(cents / 100, currency as Currency);
}

function JoinSectionHeading({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
        <Icon className="h-4 w-4 text-primary" aria-hidden />
      </div>
      <div>
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        {description ? <p className="mt-0.5 text-sm text-muted-foreground">{description}</p> : null}
      </div>
    </div>
  );
}

function JoinStepIndicator({ step, isAuthenticated }: { step: WizardStep; isAuthenticated: boolean }) {
  const items = [
    { id: 'plan' as const, label: 'Choose plan' },
    { id: 'auth' as const, label: 'Account' },
    { id: 'members' as const, label: 'Household' },
    { id: 'payment' as const, label: 'Payment' },
  ];
  const activeIndex = items.findIndex((item) => item.id === step);

  return (
    <ol className="flex flex-wrap items-center gap-2 text-sm" aria-label="Join progress">
      {items.map((item, index) => {
        const isActive = item.id === step;
        const isComplete =
          step === 'success' ||
          activeIndex > index ||
          (item.id === 'auth' && isAuthenticated && activeIndex > 0);
        const isSkipped = item.id === 'auth' && isAuthenticated;
        return (
          <li key={item.id} className="flex items-center gap-2">
            {index > 0 ? <span className="text-muted-foreground" aria-hidden>/</span> : null}
            <span
              className={cn(
                'rounded-full px-3 py-1 font-medium',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : isComplete || isSkipped
                    ? 'text-foreground'
                    : 'text-muted-foreground'
              )}
            >
              {item.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function formatPlanPrice(plan: MembershipType): string {
  const amount = formatCentsAsMoney(plan.priceCents, plan.currency);
  if (plan.termType === 'recurring') {
    return `${amount} / ${plan.billingInterval}`;
  }
  if (plan.periodAnchor === 'join_date' && plan.periodMonths) {
    return `${amount} for ${plan.periodMonths} months`;
  }
  return amount;
}

function formatPlanSeatSummary(plan: MembershipType): string {
  const adultLabel = `${plan.maxAdults} adult${plan.maxAdults === 1 ? '' : 's'}`;
  const juniorLabel =
    plan.includedJuniors > 0
      ? `, ${plan.includedJuniors} junior${plan.includedJuniors === 1 ? '' : 's'}`
      : '';
  return `Covers ${adultLabel}${juniorLabel}`;
}

function planSupportsFamilyMembers(plan: MembershipType): boolean {
  return plan.maxAdults + plan.includedJuniors > 1;
}

function MembershipPlanOption({
  plan,
  selected,
  onSelect,
}: {
  plan: MembershipType;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full rounded-xl border p-4 text-left transition-colors',
        selected
          ? 'border-primary bg-primary/5 ring-1 ring-primary/25'
          : 'border-border bg-card hover:border-primary/30'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-foreground">{plan.name}</p>
          {plan.description ? (
            <p className="mt-1 text-sm text-muted-foreground">{plan.description}</p>
          ) : null}
          <p className="mt-2 text-xs text-muted-foreground">{formatPlanSeatSummary(plan)}</p>
        </div>
        <p className="shrink-0 text-right text-sm font-semibold text-foreground">{formatPlanPrice(plan)}</p>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {plan.termType === 'recurring' ? (
          <Badge variant="secondary" className="text-xs">
            Auto-renews
          </Badge>
        ) : (
          <Badge variant="secondary" className="text-xs">
            Fixed period
          </Badge>
        )}
        {plan.requiresProof ? (
          <Badge variant="outline" className="text-xs">
            Verification required
          </Badge>
        ) : null}
      </div>
    </button>
  );
}

function planHasAgeRestrictions(plan: MembershipType): boolean {
  return plan.minAgeAtJoin != null || plan.maxAgeAtJoin != null;
}

function MembershipPriceSummary({
  membershipTypeId,
  selectedPlan,
  quote,
  quoteLoading,
  quoteError,
  quoteDetails,
  additionalSeatCount,
  primaryHasDob,
  awaitingDetails,
  totalLabel = 'Total',
}: {
  membershipTypeId: string;
  selectedPlan: MembershipType | null;
  quote: PublicMembershipQuoteResponse | null;
  quoteLoading: boolean;
  quoteError: string | null;
  quoteDetails: string[];
  additionalSeatCount: number;
  primaryHasDob: boolean;
  awaitingDetails: boolean;
  totalLabel?: string;
}) {
  const showAgeEligibilityNote =
    !awaitingDetails &&
    selectedPlan &&
    planHasAgeRestrictions(selectedPlan) &&
    !primaryHasDob &&
    additionalSeatCount === 0 &&
    !quoteError;

  return (
    <Card className="overflow-hidden border-primary/20 shadow-sm">
      <CardHeader className="border-b border-border bg-primary/5 px-5 py-4">
        <CardTitle className="text-base">Membership summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 p-5">
        <div className="space-y-3 text-sm">
          <div className="flex items-start justify-between gap-3">
            <span className="text-muted-foreground">Plan</span>
            <span className="text-right font-medium text-foreground">{selectedPlan?.name ?? '—'}</span>
          </div>
          {selectedPlan ? (
            <div className="flex items-start justify-between gap-3">
              <span className="text-muted-foreground">Billing</span>
              <span className="text-right font-medium text-foreground">{formatPlanPrice(selectedPlan)}</span>
            </div>
          ) : null}
        </div>

        <Separator />

        {!membershipTypeId ? (
          <p className="text-sm text-muted-foreground">Select a plan to see pricing.</p>
        ) : awaitingDetails ? (
          <p className="text-sm text-muted-foreground">Complete your details to see pricing.</p>
        ) : quoteLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Calculating…
          </p>
        ) : quoteError ? (
          <div className="space-y-2">
            <p className="text-sm text-destructive" role="alert">
              {quoteError}
            </p>
            {quoteDetails.map((detail) => (
              <p key={detail} className="text-xs text-destructive/90" role="alert">
                {detail}
              </p>
            ))}
            {additionalSeatCount > 0 && quoteDetails.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Check the date of birth for each family member you added.
              </p>
            ) : null}
          </div>
        ) : quote ? (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Base price</span>
              <span>{formatCentsAsMoney(quote.breakdown.basePriceCents, selectedPlan?.currency ?? 'USD')}</span>
            </div>
            {quote.breakdown.addonsTotalCents > 0 ? (
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Add-ons</span>
                <span>
                  {formatCentsAsMoney(quote.breakdown.addonsTotalCents, selectedPlan?.currency ?? 'USD')}
                </span>
              </div>
            ) : null}
            {quote.breakdown.surchargeCents > 0 ? (
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Processing fee</span>
                <span>
                  {formatCentsAsMoney(quote.breakdown.surchargeCents, selectedPlan?.currency ?? 'USD')}
                </span>
              </div>
            ) : null}
            {(quote.taxAmountCents ?? 0) > 0 ? (
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">
                  {quote.taxInclusive ? 'Includes tax' : 'Tax'}
                </span>
                <span>
                  {formatCentsAsMoney(quote.taxAmountCents ?? 0, selectedPlan?.currency ?? 'USD')}
                </span>
              </div>
            ) : null}
            <div className="flex justify-between gap-3 border-t border-border pt-2 font-semibold text-foreground">
              <span>{totalLabel}</span>
              <span>
                {formatCentsAsMoney(quote.breakdown.totalCents, selectedPlan?.currency ?? 'USD')}
              </span>
            </div>
            {quote.requiresVerification ? (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                This plan requires club verification before your membership becomes active.
              </p>
            ) : null}
            {showAgeEligibilityNote ? (
              <p className="text-xs text-muted-foreground">
                This plan has age requirements. We&apos;ll confirm your eligibility before payment.
              </p>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function PublicClubJoinContent() {
  const slug = useClubSlug();
  const slugResolving = useClubSlugResolving();
  const orgSlug = useOrgSlug();
  const { p, fields } = useClubPaths();
  const { isEmbedMode } = useEmbedHost();
  const { user, isAuthenticated, login, setUser } = useAuthStore();
  const { host: clubHost, loading: clubHostLoading } = useClubHost();
  const { host: orgHost, loading: orgHostLoading } = useOrgHost();
  const { profile: clubProfile } = usePublicClubProfile({
    slug,
    subdomainLabel: clubHost.mode === 'subdomain' ? clubHost.subdomainLabel : undefined,
    hostLoading: clubHost.mode === 'subdomain' && clubHostLoading,
    enabled: !!slug || (clubHost.mode === 'subdomain' && !!clubHost.subdomainLabel),
  });
  const { profile: orgProfile } = usePublicOrgProfile({
    slug: orgSlug,
    subdomainLabel: orgHost.mode === 'subdomain' ? orgHost.subdomainLabel : undefined,
    hostLoading: orgHost.mode === 'subdomain' && orgHostLoading,
    enabled: !!orgSlug || (orgHost.mode === 'subdomain' && !!orgHost.subdomainLabel),
  });
  const bannerUrl = orgProfile?.organization.bannerUrl ?? clubProfile?.club.bannerUrl ?? null;
  const defaultPhoneCountry = useDefaultPhoneCountry();

  const [catalog, setCatalog] = useState<PublicMembershipCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [notAvailableReason, setNotAvailableReason] = useState<string | null>(null);

  const [membershipTypeId, setMembershipTypeId] = useState('');
  const [additionalSeats, setAdditionalSeats] = useState<PublicMembershipSeatInput[]>([]);
  const [selectedAddonIds, setSelectedAddonIds] = useState<string[]>([]);
  const [showAddSeatForm, setShowAddSeatForm] = useState(false);
  const [seatFirstName, setSeatFirstName] = useState('');
  const [seatLastName, setSeatLastName] = useState('');
  const [seatDob, setSeatDob] = useState('');
  const [seatEmail, setSeatEmail] = useState('');
  const [seatEmailLookup, setSeatEmailLookup] = useState<
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'matched'; member: { id: string; firstName: string; lastName: string } }
    | { status: 'no_match' }
  >({ status: 'idle' });

  const [primaryFirstName, setPrimaryFirstName] = useState('');
  const [primaryLastName, setPrimaryLastName] = useState('');
  const [primaryGender, setPrimaryGender] = useState<'' | PrimaryGender>('');
  const [primaryDob, setPrimaryDob] = useState('');
  const [primaryPhone, setPrimaryPhone] = useState('');
  const [primaryPhoneError, setPrimaryPhoneError] = useState<string | null>(null);
  const [primaryDetailsInitialized, setPrimaryDetailsInitialized] = useState(false);
  const [primaryDetailsTouched, setPrimaryDetailsTouched] = useState(false);

  const [quote, setQuote] = useState<PublicMembershipQuoteResponse | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoteDetails, setQuoteDetails] = useState<string[]>([]);

  const [step, setStep] = useState<WizardStep>('plan');

  const [authMode, setAuthMode] = useState<AuthMode>('register');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authConfirmPassword, setAuthConfirmPassword] = useState('');
  const [authFirstName, setAuthFirstName] = useState('');
  const [authLastName, setAuthLastName] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [authSubmitting, setAuthSubmitting] = useState(false);

  const [joinSubmitting, setJoinSubmitting] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joinDetails, setJoinDetails] = useState<string[]>([]);
  const [purchase, setPurchase] = useState<MembershipPurchaseResponse | null>(null);

  useEffect(() => {
    if (!slug) {
      if (!slugResolving) {
        setLoading(false);
        setNotAvailableReason('This club page is unavailable.');
      }
      return;
    }
    setLoading(true);
    void publicClubMembershipService.getCatalog(slug).then((res) => {
      if (res.success && res.data) {
        setCatalog(res.data);
        setNotAvailableReason(null);
      } else {
        setNotAvailableReason(res.error || 'Online membership join is not available for this club');
      }
      setLoading(false);
    });
  }, [slug, slugResolving]);

  useEffect(() => {
    if (isAuthenticated && step === 'auth') {
      setStep('members');
    }
  }, [isAuthenticated, step]);

  useEffect(() => {
    if (!user || primaryDetailsInitialized) return;
    setPrimaryFirstName(user.firstName || authFirstName || '');
    setPrimaryLastName(user.lastName || authLastName || '');
    setPrimaryPhone(user.phone || '');
    setPrimaryDetailsInitialized(true);
  }, [user, authFirstName, authLastName, primaryDetailsInitialized]);

  useEffect(() => {
    if (step === 'members') {
      setPrimaryDetailsTouched(false);
      setQuote(null);
      setQuoteError(null);
      setQuoteDetails([]);
      setQuoteLoading(false);
    }
  }, [step]);

  const markPrimaryDetailsTouched = () => {
    setPrimaryDetailsTouched(true);
  };

  const selectedPlan = catalog?.membershipTypes.find((t) => t.id === membershipTypeId) ?? null;

  const primaryMemberPayload = useMemo((): PublicMembershipPrimaryMemberInput | null => {
    const firstName = primaryFirstName.trim();
    const lastName = primaryLastName.trim();
    if (!firstName || !lastName || !primaryGender) return null;
    if (primaryPhone.trim() && !isValidPhone(primaryPhone, defaultPhoneCountry)) return null;
    return {
      firstName,
      lastName,
      gender: primaryGender,
      dateOfBirth: primaryDob || null,
      ...(primaryPhone.trim() ? { phone: primaryPhone.trim() } : {}),
    };
  }, [primaryFirstName, primaryLastName, primaryGender, primaryDob, primaryPhone, defaultPhoneCountry]);

  const primaryDetailsComplete = useMemo(() => {
    if (!primaryMemberPayload) return false;
    if (primaryPhone.trim() && primaryPhoneError) return false;
    if (selectedPlan && planHasAgeRestrictions(selectedPlan) && !primaryDob) return false;
    return true;
  }, [primaryMemberPayload, primaryPhone, primaryPhoneError, selectedPlan, primaryDob]);

  const addonSelectionsPayload = useMemo(
    () => selectedAddonIds.map((addonId) => ({ addonId })),
    [selectedAddonIds]
  );

  useEffect(() => {
    if (!slug || !membershipTypeId || (step !== 'members' && step !== 'payment')) {
      if (step === 'plan' || step === 'auth') {
        setQuote(null);
        setQuoteError(null);
        setQuoteDetails([]);
      }
      return undefined;
    }
    if (!isAuthenticated || !primaryMemberPayload || !primaryDetailsTouched) {
      return undefined;
    }
    setQuoteLoading(true);
    const timer = window.setTimeout(async () => {
      const res = await publicClubMembershipService.quote(
        slug,
        {
          membershipTypeId,
          primaryMember: primaryMemberPayload,
          additionalSeats,
          addonSelections: addonSelectionsPayload,
        },
        { authenticated: true }
      );
      if (res.success && res.data) {
        setQuote(res.data);
        setQuoteError(null);
        setQuoteDetails([]);
      } else {
        setQuote(null);
        setQuoteError(res.error || 'Failed to price this membership');
        setQuoteDetails((res as { details?: string[] }).details ?? []);
      }
      setQuoteLoading(false);
    }, 400);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    slug,
    membershipTypeId,
    step,
    isAuthenticated,
    primaryMemberPayload,
    primaryDetailsTouched,
    JSON.stringify(additionalSeats),
    JSON.stringify(addonSelectionsPayload),
  ]);

  useEffect(() => {
    if (!slug || !seatEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(seatEmail.trim())) {
      setSeatEmailLookup({ status: 'idle' });
      return undefined;
    }

    setSeatEmailLookup({ status: 'loading' });
    const timer = window.setTimeout(async () => {
      const res = await publicClubMembershipService.lookupMemberEmail(slug, seatEmail.trim());
      if (res.success && res.data?.match) {
        setSeatEmailLookup({ status: 'matched', member: res.data.member });
        setSeatFirstName(res.data.member.firstName);
        setSeatLastName(res.data.member.lastName);
      } else {
        setSeatEmailLookup({ status: 'no_match' });
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [slug, seatEmail]);

  const addSeat = () => {
    if (!seatFirstName.trim() || !seatLastName.trim() || !selectedPlan) return;

    const nextSeat: PublicMembershipSeatInput = {
      firstName: seatFirstName.trim(),
      lastName: seatLastName.trim(),
      dateOfBirth: seatDob || null,
      email: seatEmail.trim() || null,
    };
    const projectedSeats = [...additionalSeats, nextSeat];
    const seatValidation = validateMembershipSeats(
      selectedPlan,
      buildAnonymousJoinSeatTypes(projectedSeats)
    );
    if (!seatValidation.valid) {
      toast.error(seatValidation.errors[0] ?? 'This plan does not support the selected seats');
      return;
    }

    setAdditionalSeats(projectedSeats);
    markPrimaryDetailsTouched();
    setSeatFirstName('');
    setSeatLastName('');
    setSeatDob('');
    setSeatEmail('');
    setSeatEmailLookup({ status: 'idle' });
    setShowAddSeatForm(false);
  };

  const removeSeat = (index: number) => {
    markPrimaryDetailsTouched();
    setAdditionalSeats((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleAddon = (addonId: string) => {
    markPrimaryDetailsTouched();
    setSelectedAddonIds((prev) => (prev.includes(addonId) ? prev.filter((id) => id !== addonId) : [...prev, addonId]));
  };

  const abandonCheckout = async () => {
    if (!slug || !purchase?.membershipId) return;
    await publicClubMembershipService.cancelCheckout(slug, purchase.membershipId);
  };

  const handlePaymentBack = () => {
    void abandonCheckout().finally(() => {
      setPurchase(null);
      setJoinError(null);
      setJoinDetails([]);
      setStep('members');
    });
  };

  const startJoin = async () => {
    if (!slug) {
      setJoinError('This club page is unavailable.');
      return;
    }
    if (!primaryMemberPayload) {
      setJoinError('Please complete your details on the household step before paying.');
      return;
    }
    setJoinSubmitting(true);
    setJoinError(null);
    setJoinDetails([]);
    try {
      const res = await publicClubMembershipService.join(slug, {
        membershipTypeId,
        primaryMember: primaryMemberPayload,
        additionalSeats,
        addonSelections: addonSelectionsPayload,
      });
      if (res.success && res.data?.clientSecret) {
        setPurchase(res.data);
      } else if (res.success && res.data) {
        setPurchase(null);
        setJoinError('Payment could not be started. Please try again or contact the club.');
      } else {
        setPurchase(null);
        setJoinError(res.error || 'Failed to start your membership');
        setJoinDetails((res as { details?: string[] }).details ?? []);
      }
    } catch (error) {
      setPurchase(null);
      setJoinError(error instanceof Error ? error.message : 'Failed to start your membership');
    } finally {
      setJoinSubmitting(false);
    }
  };

  const handleContinueFromPlan = () => {
    if (!membershipTypeId) return;
    setStep(isAuthenticated ? 'members' : 'auth');
  };

  const handleContinueFromMembers = () => {
    if (!membershipTypeId || !quote || !primaryDetailsComplete) return;
    if (primaryPhone.trim() && !isValidPhone(primaryPhone, defaultPhoneCountry)) {
      setPrimaryPhoneError('Enter a valid phone number');
      return;
    }
    setJoinError(null);
    setJoinDetails([]);
    setPurchase(null);
    setStep('payment');
  };

  useEffect(() => {
    if (step !== 'payment' || !isAuthenticated || purchase || joinSubmitting) return;
    if (!primaryMemberPayload) {
      setJoinError('Please complete your details on the household step before paying.');
      return;
    }
    if (joinError) return;
    void startJoin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, isAuthenticated, primaryMemberPayload, membershipTypeId]);

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authEmail.trim()) {
      toast.error('Please complete all required fields');
      return;
    }
    if (authMode === 'login') {
      if (!authPassword) {
        toast.error('Please complete all required fields');
        return;
      }
    } else if (!authFirstName.trim() || !authLastName.trim()) {
      toast.error('Please complete all required fields');
      return;
    }
    setAuthSubmitting(true);
    try {
      if (authMode === 'login') {
        await login(authEmail.trim(), authPassword);
        setStep('members');
        return;
      }

      if (!termsAccepted || !privacyAccepted) {
        toast.error('You must accept the Terms of Service and Privacy Policy');
        return;
      }
      if (authPassword !== authConfirmPassword) {
        toast.error("Passwords don't match");
        return;
      }
      if (authPassword.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }

      const { user: createdUser } = await AuthService.signUpAsGuardian(
        authEmail.trim(),
        authPassword,
        authFirstName.trim(),
        authLastName.trim(),
        undefined,
        {
          termsAccepted,
          privacyAccepted,
          termsVersion: LEGAL_VERSIONS.TERMS,
          privacyVersion: LEGAL_VERSIONS.PRIVACY,
        }
      );
      setUser(createdUser);
      setStep('members');
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string };
      if (err.code === 'auth/email-already-in-use') {
        toast.error('An account with this email already exists. Please sign in instead.');
        setAuthMode('login');
      } else {
        toast.error(err.message || 'Could not create your account');
      }
    } finally {
      setAuthSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-label="Loading" />
      </div>
    );
  }

  if (notAvailableReason || !catalog) {
    return (
      <div className="mx-auto max-w-lg px-4 py-12 text-center sm:px-6">
        <h1 className="text-xl font-semibold text-foreground">Membership join unavailable</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {notAvailableReason || 'This club is not accepting online membership join right now.'}
        </p>
      </div>
    );
  }

  const membershipPath = p?.('/membership') ?? (slug ? `/club/${slug}/membership` : '/');
  const loginPath = fields
    ? (isEmbedMode
        ? buildClubAuthLinkOutUrl(fields, '/login', membershipPath)
        : p?.(`/login?returnUrl=${encodeURIComponent(membershipPath)}`) ?? membershipPath)
    : membershipPath;
  const registerPath = fields
    ? (isEmbedMode
        ? buildClubAuthLinkOutUrl(fields, '/register', membershipPath)
        : p?.(`/register?returnUrl=${encodeURIComponent(membershipPath)}`) ?? membershipPath)
    : membershipPath;

  return (
    <>
      <PublicOrgSubpageHero
        bannerUrl={bannerUrl}
        title={`Join ${catalog.club.name}`}
        headingId="join-hero-heading"
      >
        <p className="mt-3 max-w-2xl text-sm text-white/85 sm:text-base">
          Become a member online in a few minutes
        </p>
      </PublicOrgSubpageHero>

      <div className="bg-muted/30">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:py-10">
          {step !== 'success' ? (
            <div className="mb-6">
              <JoinStepIndicator step={step} isAuthenticated={isAuthenticated} />
            </div>
          ) : null}

          {step === 'success' ? (
            <div className="mx-auto flex max-w-md justify-center py-4 sm:max-w-lg sm:py-8">
              <Card className="w-full overflow-hidden border-emerald-200/70 bg-card shadow-sm dark:border-emerald-900/50">
                <CardContent className="space-y-6 px-6 py-10 text-center sm:px-10 sm:py-12">
                  <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
                    <CheckCircle2 className="h-11 w-11 text-emerald-600 dark:text-emerald-400" aria-hidden />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-2xl font-semibold tracking-tight text-foreground">You&apos;re in!</h2>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      Welcome to {catalog.club.name}. Your membership is being set up and a confirmation email is on
                      its way.
                    </p>
                  </div>
                  {slug ? (
                    <Button asChild className="w-full sm:w-auto">
                      <Link to={membershipPath}>View your membership</Link>
                    </Button>
                  ) : null}
                </CardContent>
              </Card>
            </div>
          ) : (
          <div
            className={cn(
              'grid gap-8',
              step === 'plan' || step === 'members' || step === 'payment'
                ? 'lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start'
                : 'max-w-2xl'
            )}
          >
            <Card className="overflow-hidden border-border/80 bg-card shadow-sm">
              <CardHeader className="border-b border-border bg-muted/20 px-6 py-5">
                <CardTitle className="text-lg">
                  {step === 'payment'
                    ? 'Complete payment'
                    : step === 'members'
                      ? 'Add household members'
                      : step === 'auth'
                        ? 'Create your account'
                        : 'Choose your membership'}
                </CardTitle>
                <CardDescription>
                  {step === 'payment'
                    ? 'Enter your card details to activate your membership.'
                    : step === 'members'
                      ? 'Add anyone else covered by this plan, then review pricing before payment.'
                      : step === 'auth'
                        ? 'Create a CourtSync account to continue. Already a member? Sign in instead.'
                        : 'Review the plans below and choose the one that fits your household.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                {step === 'payment' ? (
                  purchase && purchase.clientSecret ? (
                    <div className="space-y-6">
                      <div className="lg:hidden">
                        <MembershipPriceSummary
                          membershipTypeId={membershipTypeId}
                          selectedPlan={selectedPlan}
                          quote={quote}
                          quoteLoading={quoteLoading}
                          quoteError={quoteError}
                          quoteDetails={quoteDetails}
                          additionalSeatCount={additionalSeats.length}
                          primaryHasDob={!!primaryDob}
                          awaitingDetails={false}
                          totalLabel="Total due today"
                        />
                      </div>
                      <PublicStripePaymentConfirm
                        clientSecret={purchase.clientSecret}
                        stripeConnectAccountId={purchase.stripeConnectAccountId}
                        amountCents={purchase.totalCents}
                        currency={purchase.currency}
                        onSuccess={() => setStep('success')}
                        onCancel={handlePaymentBack}
                      />
                    </div>
                  ) : joinSubmitting ? (
                    <p className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                      Setting up your membership…
                    </p>
                  ) : (
                    <div className="space-y-4 text-center">
                      <p className="text-sm text-destructive" role="alert">
                        {joinError || 'Something went wrong'}
                      </p>
                      {joinDetails.length > 0 ? (
                        <ul className="space-y-1 text-left text-sm text-muted-foreground">
                          {joinDetails.map((detail) => (
                            <li key={detail}>{detail}</li>
                          ))}
                        </ul>
                      ) : null}
                      <div className="flex justify-center gap-2">
                        <Button type="button" variant="outline" onClick={handlePaymentBack}>
                          Back
                        </Button>
                        <Button type="button" onClick={startJoin} isLoading={joinSubmitting}>
                          Try again
                        </Button>
                      </div>
                    </div>
                  )
                ) : step === 'auth' ? (
                  isEmbedMode ? (
                    <div className="space-y-4 text-center text-sm text-muted-foreground">
                      <p>Sign in or create an account on the club website to continue your membership join.</p>
                      <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
                        <Button asChild variant="outline">
                          <a href={loginPath} target="_blank" rel="noopener noreferrer">
                            Sign in
                          </a>
                        </Button>
                        <Button asChild>
                          <a href={registerPath} target="_blank" rel="noopener noreferrer">
                            Create account
                          </a>
                        </Button>
                      </div>
                      <Button type="button" variant="ghost" onClick={() => setStep('plan')}>
                        Back
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleAuthSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <button
                          type="button"
                          className={cn(
                            'rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                            authMode === 'register'
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border text-muted-foreground hover:bg-muted'
                          )}
                          onClick={() => setAuthMode('register')}
                        >
                          Create account
                        </button>
                        <button
                          type="button"
                          className={cn(
                            'rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                            authMode === 'login'
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border text-muted-foreground hover:bg-muted'
                          )}
                          onClick={() => setAuthMode('login')}
                        >
                          Sign in
                        </button>
                      </div>

                      {authMode === 'register' ? (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <Field>
                            <FieldLabel htmlFor="auth-first-name">First name</FieldLabel>
                            <Input
                              id="auth-first-name"
                              value={authFirstName}
                              onChange={(e) => setAuthFirstName(e.target.value)}
                              required
                            />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="auth-last-name">Last name</FieldLabel>
                            <Input
                              id="auth-last-name"
                              value={authLastName}
                              onChange={(e) => setAuthLastName(e.target.value)}
                              required
                            />
                          </Field>
                        </div>
                      ) : null}

                      <Field>
                        <FieldLabel htmlFor="auth-email">Email</FieldLabel>
                        <Input
                          id="auth-email"
                          type="email"
                          value={authEmail}
                          onChange={(e) => setAuthEmail(e.target.value)}
                          required
                        />
                      </Field>

                      <Field>
                        <FieldLabel htmlFor="auth-password">Password</FieldLabel>
                        <Input
                          id="auth-password"
                          type="password"
                          value={authPassword}
                          onChange={(e) => setAuthPassword(e.target.value)}
                          required
                        />
                      </Field>

                      {authMode === 'register' ? (
                        <>
                          <Field>
                            <FieldLabel htmlFor="auth-confirm-password">Confirm password</FieldLabel>
                            <Input
                              id="auth-confirm-password"
                              type="password"
                              value={authConfirmPassword}
                              onChange={(e) => setAuthConfirmPassword(e.target.value)}
                              required
                            />
                          </Field>
                          <div className="space-y-3">
                            <label className="flex items-start gap-2 text-sm">
                              <Checkbox checked={termsAccepted} onCheckedChange={(v) => setTermsAccepted(!!v)} />
                              <span>I accept the Terms of Service</span>
                            </label>
                            <label className="flex items-start gap-2 text-sm">
                              <Checkbox checked={privacyAccepted} onCheckedChange={(v) => setPrivacyAccepted(!!v)} />
                              <span>I accept the Privacy Policy</span>
                            </label>
                          </div>
                        </>
                      ) : null}

                      <div className="flex gap-2">
                        <Button type="button" variant="outline" className="flex-1" onClick={() => setStep('plan')}>
                          Back
                        </Button>
                        <Button type="submit" className="flex-1" isLoading={authSubmitting}>
                          {authMode === 'login' ? 'Sign in' : 'Create account'}
                        </Button>
                      </div>
                    </form>
                  )
                ) : step === 'members' ? (
                  <div className="space-y-8">
                    {selectedPlan ? (
                      <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm">
                        <p className="font-medium text-foreground">{selectedPlan.name}</p>
                        <p className="mt-1 text-muted-foreground">{formatPlanPrice(selectedPlan)}</p>
                      </div>
                    ) : null}

                    {isAuthenticated && user ? (
                      <section className="space-y-4" aria-labelledby="join-primary-heading">
                        <JoinSectionHeading
                          icon={User}
                          title="Your details"
                          description="Confirm your name and contact details for this membership"
                        />
                        <div className="space-y-4 pl-0 sm:pl-12">
                          <p className="text-sm text-muted-foreground">
                            Signed in as <span className="font-medium text-foreground">{user.email}</span>
                          </p>
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <Field>
                              <FieldLabel htmlFor="primary-first-name">First name</FieldLabel>
                              <Input
                                id="primary-first-name"
                                value={primaryFirstName}
                                onChange={(e) => {
                                  markPrimaryDetailsTouched();
                                  setPrimaryFirstName(e.target.value);
                                }}
                                autoComplete="given-name"
                                required
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="primary-last-name">Last name</FieldLabel>
                              <Input
                                id="primary-last-name"
                                value={primaryLastName}
                                onChange={(e) => {
                                  markPrimaryDetailsTouched();
                                  setPrimaryLastName(e.target.value);
                                }}
                                autoComplete="family-name"
                                required
                              />
                            </Field>
                          </div>
                          <Field>
                            <FieldLabel htmlFor="primary-gender">Gender</FieldLabel>
                            <Select
                              value={primaryGender || undefined}
                              onValueChange={(value) => {
                                markPrimaryDetailsTouched();
                                setPrimaryGender(value as PrimaryGender);
                              }}
                            >
                              <SelectTrigger id="primary-gender">
                                <SelectValue placeholder="Select gender" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="male">Male</SelectItem>
                                <SelectItem value="female">Female</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="primary-dob">
                              Date of birth
                              {selectedPlan && planHasAgeRestrictions(selectedPlan) ? ' (required)' : ''}
                            </FieldLabel>
                            <Input
                              id="primary-dob"
                              type="date"
                              value={primaryDob}
                              onChange={(e) => {
                                markPrimaryDetailsTouched();
                                setPrimaryDob(e.target.value);
                              }}
                              required={!!selectedPlan && planHasAgeRestrictions(selectedPlan)}
                            />
                          </Field>
                          <Field>
                            <PhoneNumberLabel htmlFor="primary-phone" purpose="general">
                              Mobile number (optional)
                            </PhoneNumberLabel>
                            <PhoneInput
                              id="primary-phone"
                              value={primaryPhone}
                              onChange={(value) => {
                                markPrimaryDetailsTouched();
                                setPrimaryPhone(value);
                                if (primaryPhoneError) setPrimaryPhoneError(null);
                              }}
                              defaultCountry={defaultPhoneCountry}
                              error={!!primaryPhoneError}
                              aria-invalid={!!primaryPhoneError}
                              aria-describedby={primaryPhoneError ? 'primary-phone-error' : undefined}
                            />
                            {primaryPhoneError ? (
                              <p id="primary-phone-error" className="mt-1 text-sm text-destructive" role="alert">
                                {primaryPhoneError}
                              </p>
                            ) : null}
                          </Field>
                        </div>
                      </section>
                    ) : null}

                    {selectedPlan && planSupportsFamilyMembers(selectedPlan) ? (
                      <section className="space-y-4" aria-labelledby="join-family-heading">
                        <JoinSectionHeading
                          icon={Users}
                          title="Household members"
                          description="Add your partner, children, or other people covered by this plan"
                        />
                        <div className="space-y-3 pl-0 sm:pl-12">
                          <p className="text-sm text-muted-foreground">{describePlanSeatAllowance(selectedPlan)}</p>
                          <p className="text-xs text-muted-foreground">
                            Enter a date of birth for children so they count as juniors. Email is optional — if it
                            matches an existing club member, we&apos;ll link their profile automatically.
                          </p>
                          {additionalSeats.length > 0 ? (
                            <ul className="space-y-2">
                              {additionalSeats.map((seat, index) => (
                                <li
                                  key={index}
                                  className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm"
                                >
                                  <span>
                                    {seat.firstName} {seat.lastName}
                                    {seat.email ? (
                                      <span className="mt-0.5 block text-xs text-muted-foreground">{seat.email}</span>
                                    ) : null}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => removeSeat(index)}
                                    className="text-muted-foreground hover:text-foreground"
                                    aria-label={`Remove ${seat.firstName}`}
                                  >
                                    <X className="h-4 w-4" aria-hidden />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          ) : null}

                          {showAddSeatForm ? (
                            <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
                              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <Field>
                                  <FieldLabel htmlFor="seat-first">First name</FieldLabel>
                                  <Input
                                    id="seat-first"
                                    value={seatFirstName}
                                    onChange={(e) => setSeatFirstName(e.target.value)}
                                  />
                                </Field>
                                <Field>
                                  <FieldLabel htmlFor="seat-last">Last name</FieldLabel>
                                  <Input
                                    id="seat-last"
                                    value={seatLastName}
                                    onChange={(e) => setSeatLastName(e.target.value)}
                                  />
                                </Field>
                              </div>
                              <Field>
                                <FieldLabel htmlFor="seat-email">Email (optional)</FieldLabel>
                                <Input
                                  id="seat-email"
                                  type="email"
                                  value={seatEmail}
                                  onChange={(e) => setSeatEmail(e.target.value)}
                                  autoComplete="email"
                                />
                                {seatEmailLookup.status === 'loading' ? (
                                  <p className="mt-1 text-xs text-muted-foreground">Checking email…</p>
                                ) : seatEmailLookup.status === 'matched' ? (
                                  <p className="mt-1 text-xs text-primary">
                                    Linked to existing member {seatEmailLookup.member.firstName}{' '}
                                    {seatEmailLookup.member.lastName}
                                  </p>
                                ) : null}
                              </Field>
                              <Field>
                                <FieldLabel htmlFor="seat-dob">Date of birth</FieldLabel>
                                <Input
                                  id="seat-dob"
                                  type="date"
                                  value={seatDob}
                                  onChange={(e) => setSeatDob(e.target.value)}
                                />
                              </Field>
                              <div className="flex justify-end gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setShowAddSeatForm(false);
                                    setSeatEmail('');
                                    setSeatEmailLookup({ status: 'idle' });
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  disabled={!seatFirstName.trim() || !seatLastName.trim()}
                                  onClick={addSeat}
                                >
                                  Add member
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Button type="button" variant="outline" size="sm" onClick={() => setShowAddSeatForm(true)}>
                              <UserPlus className="mr-2 h-4 w-4" aria-hidden />
                              Add a household member
                            </Button>
                          )}
                        </div>
                      </section>
                    ) : null}

                    {catalog.addons.length > 0 ? (
                      <>
                        <Separator />
                        <section className="space-y-4" aria-labelledby="join-addons-heading">
                          <JoinSectionHeading
                            icon={CreditCard}
                            title="Add-ons"
                            description="Optional extras you can include with your membership"
                          />
                          <div className="space-y-2 pl-0 sm:pl-12">
                            {catalog.addons.map((addon) => (
                              <label
                                key={addon.id}
                                className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 text-sm transition-colors hover:bg-muted/30"
                              >
                                <Checkbox
                                  checked={selectedAddonIds.includes(addon.id)}
                                  onCheckedChange={() => toggleAddon(addon.id)}
                                  className="mt-0.5"
                                />
                                <span className="min-w-0">
                                  <span className="font-medium text-foreground">{addon.name}</span>
                                  <span className="mt-0.5 block text-muted-foreground">
                                    +{formatCentsAsMoney(addon.priceCents, addon.currency)}
                                    {addon.pricingMode === 'per_adult' ? ' per adult' : ''}
                                  </span>
                                </span>
                              </label>
                            ))}
                          </div>
                        </section>
                      </>
                    ) : null}

                    <div className="lg:hidden">
                      <MembershipPriceSummary
                        membershipTypeId={membershipTypeId}
                        selectedPlan={selectedPlan}
                        quote={quote}
                        quoteLoading={quoteLoading}
                        quoteError={quoteError}
                        quoteDetails={quoteDetails}
                        additionalSeatCount={additionalSeats.length}
                        primaryHasDob={!!primaryDob}
                        awaitingDetails={!primaryDetailsTouched}
                      />
                    </div>

                    <div className="flex gap-2 lg:hidden">
                      <Button type="button" variant="outline" className="flex-1" onClick={() => setStep('plan')}>
                        Back
                      </Button>
                      <Button
                        type="button"
                        className="flex-1"
                        disabled={!membershipTypeId || !quote || !primaryDetailsComplete}
                        onClick={handleContinueFromMembers}
                      >
                        Continue to payment
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-8">
                    <section className="space-y-4" aria-labelledby="join-plan-heading">
                      <JoinSectionHeading
                        icon={Shield}
                        title="Membership plan"
                        description="Choose the plan that fits you best"
                      />
                      <div className="space-y-3 pl-0 sm:pl-12">
                        {catalog.membershipTypes.map((plan) => (
                          <MembershipPlanOption
                            key={plan.id}
                            plan={plan}
                            selected={membershipTypeId === plan.id}
                            onSelect={() => setMembershipTypeId(plan.id)}
                          />
                        ))}
                      </div>
                    </section>

                    <Button
                      type="button"
                      className="w-full lg:hidden"
                      disabled={!membershipTypeId}
                      onClick={handleContinueFromPlan}
                    >
                      Continue
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {step === 'plan' ? (
              <aside className="hidden lg:sticky lg:top-6 lg:block">
                <Card className="overflow-hidden border-primary/20 shadow-sm">
                  <CardHeader className="border-b border-border bg-primary/5 px-5 py-4">
                    <CardTitle className="text-base">Selected plan</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 p-5 text-sm">
                    {selectedPlan ? (
                      <>
                        <div>
                          <p className="font-medium text-foreground">{selectedPlan.name}</p>
                          <p className="mt-1 text-muted-foreground">{formatPlanPrice(selectedPlan)}</p>
                          <p className="mt-2 text-xs text-muted-foreground">{formatPlanSeatSummary(selectedPlan)}</p>
                        </div>
                        <p className="text-muted-foreground">
                          You&apos;ll create your account next, then add any other household members before payment.
                        </p>
                      </>
                    ) : (
                      <p className="text-muted-foreground">Select a plan to continue.</p>
                    )}
                  </CardContent>
                </Card>
                <Button
                  type="button"
                  className="mt-4 w-full"
                  disabled={!membershipTypeId}
                  onClick={handleContinueFromPlan}
                >
                  Continue
                </Button>
              </aside>
            ) : step === 'members' ? (
              <aside className="hidden lg:sticky lg:top-6 lg:block">
                <MembershipPriceSummary
                  membershipTypeId={membershipTypeId}
                  selectedPlan={selectedPlan}
                  quote={quote}
                  quoteLoading={quoteLoading}
                  quoteError={quoteError}
                  quoteDetails={quoteDetails}
                  additionalSeatCount={additionalSeats.length}
                  primaryHasDob={!!primaryDob}
                  awaitingDetails={!primaryDetailsTouched}
                />
                <div className="mt-4 flex gap-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setStep('plan')}>
                    Back
                  </Button>
                  <Button
                    type="button"
                    className="flex-1"
                    disabled={!membershipTypeId || !quote || !primaryDetailsComplete}
                    onClick={handleContinueFromMembers}
                  >
                    Continue
                  </Button>
                </div>
              </aside>
            ) : step === 'payment' ? (
              <aside className="hidden lg:sticky lg:top-6 lg:block">
                <MembershipPriceSummary
                  membershipTypeId={membershipTypeId}
                  selectedPlan={selectedPlan}
                  quote={quote}
                  quoteLoading={quoteLoading}
                  quoteError={quoteError}
                  quoteDetails={quoteDetails}
                  additionalSeatCount={additionalSeats.length}
                  primaryHasDob={!!primaryDob}
                  awaitingDetails={false}
                  totalLabel="Total due today"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="mt-4 w-full"
                  onClick={handlePaymentBack}
                >
                  Back
                </Button>
              </aside>
            ) : null}
          </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function PublicClubJoinPage() {
  return (
    <PublicClubRouteShell>
      <PublicClubJoinContent />
    </PublicClubRouteShell>
  );
}
